import React, { useEffect, useRef, useState } from 'react';
import { Mail, Linkedin, Github, Twitter, Send, MapPin, Loader2, ArrowRight, CalendarDays, Video, Code, BookOpen } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MEETING_URL = process.env.REACT_APP_MEETING_URL || 'mailto:tushar80rt@gmail.com?subject=Meeting%20Request';

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [selectedMeetingDate, setSelectedMeetingDate] = useState('');
  const [selectedMeetingTime, setSelectedMeetingTime] = useState('11:00 AM');
  const sectionRef = useRef(null);
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setSelectedMeetingDate(tomorrow.toISOString().split('T')[0]);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      if (response.data.success) {
        toast({
          title: 'Message Sent Successfully!',
          description: "I'll get back to you as soon as possible.",
          duration: 5000,
          className: "bg-[#0a0a0a] border border-white/10 text-white"
        });
        
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        toast({
          title: 'Message Received But Not Delivered',
          description: response.data.message || 'Your message was saved, but email notification failed.',
          variant: 'destructive',
          duration: 5000
        });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: 'Sending Failed',
        description: 'Please try again later or email me directly.',
        variant: "destructive",
        duration: 5000
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const meetingTimeSlots = ['11:00 AM', '2:00 PM', '5:00 PM'];
  const meetingRequestHref = `mailto:tushar80rt@gmail.com?subject=${encodeURIComponent(
    `Meeting Request - ${selectedMeetingDate || 'Preferred Date'} ${selectedMeetingTime}`
  )}&body=${encodeURIComponent(
    `Hi Tushar,\n\nI would like to book a meeting.\nPreferred Date: ${selectedMeetingDate || 'Not selected'}\nPreferred Time: ${selectedMeetingTime}\n\nPlease confirm availability.\n\nThanks.`
  )}`;

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'tushar80rt@gmail.com',
      href: 'mailto:tushar80rt@gmail.com',
      color: '#00D9FF'
    },
    {
      icon: MapPin,
      label: 'Location',
      value: 'Remote / Worldwide',
      href: null,
      color: '#FF00FF'
    }
  ];

  const socialLinks = [
    { icon: Linkedin, href: 'https://www.linkedin.com/in/tushar-singh-1ba975296', color: '#0077b5' },
    { icon: Github, href: 'https://github.com/tushar80rt', color: '#ffffff' },
    { icon: Twitter, href: 'https://x.com/_tusharrathore', color: '#1DA1F2' },
    { icon: Code, href: 'https://leetcode.com/u/tushar_rathore/', color: '#FFA116' },
    { icon: BookOpen, href: 'https://dev.to/_tusharrathore', color: '#ffffff' }
  ];

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="relative py-20 px-6 overflow-hidden"
      style={{
        background: 'var(--bg-primary)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute top-0 right-0 w-[500px] h-[500px] opacity-10"
          style={{
            background: 'radial-gradient(circle, var(--brand-primary) 0%, transparent 70%)',
            filter: 'blur(80px)',
            transform: 'translate(30%, -30%)'
          }}
        />
        <div 
          className="absolute bottom-0 left-0 w-[500px] h-[500px] opacity-10"
          style={{
            background: 'radial-gradient(circle, #00FF88 0%, transparent 70%)',
            filter: 'blur(80px)',
            transform: 'translate(-30%, 30%)'
          }}
        />
      </div>

      <div className="max-w-6xl mx-auto w-full relative z-10">
        {/* Section Title */}
        <div className={`text-center mb-16 ${isVisible ? 'animate-fade-down' : 'opacity-0'}`}>
          <span className="text-xs font-bold tracking-[0.3em] uppercase text-brand-primary mb-3 block">
            Let's Collaborate
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-[var(--text-primary)]">
            Get In <span style={{ color: 'var(--brand-primary)' }}>Touch</span>
          </h2>
          <p className="text-[var(--text-secondary)] max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            Have a project in mind or want to discuss AI solutions? 
            I'm always open to new opportunities and interesting conversations.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10 items-start">
          {/* Contact Info Column (Left) */}
          <div className={`lg:col-span-2 space-y-6 ${isVisible ? 'animate-fade-right' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
            {/* Info Card */}
            <div className="glass-card p-8 rounded-2xl relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <h3 className="text-xl font-bold text-[var(--text-primary)] mb-6">Contact Details</h3>
              
              <div className="space-y-6">
                {contactInfo.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-4">
                      <div className="p-3 rounded-lg bg-[var(--bg-overlay)] border border-[var(--border-subtle)] text-[var(--brand-primary)]">
                        <Icon size={20} style={{ color: item.color }} />
                      </div>
                      <div>
                        <p className="text-xs text-[var(--text-muted)] mb-1">{item.label}</p>
                        {item.href ? (
                          <a href={item.href} className="text-sm font-medium text-[var(--text-primary)] hover:text-[var(--brand-primary)] transition-colors">
                            {item.value}
                          </a>
                        ) : (
                          <p className="text-sm font-medium text-[var(--text-primary)]">{item.value}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Social Links Row */}
              <div className="mt-8 pt-8 border-t border-white/10">
                <p className="text-xs text-[var(--text-muted)] mb-4">Social Profiles</p>
                <div className="flex gap-3">
                  {socialLinks.map((social, index) => {
                    const Icon = social.icon;
                    return (
                      <a
                        key={index}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-3 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 hover:scale-110 transition-all duration-300 group/icon"
                      >
                        <Icon size={18} style={{ color: social.color }} />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Availability Badge */}
            <div className="glass-card p-6 rounded-2xl border border-green-500/20 bg-green-500/5">
              <div className="flex items-center gap-3 mb-2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
                <p className="text-sm font-semibold text-green-400">Available for Freelance</p>
              </div>
              <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
                Currently accepting new projects for AI integration, RAG pipelines, and Full-Stack development.
              </p>
            </div>

            {/* Meeting Call Section */}
            <div className="glass-card p-6 rounded-2xl border border-cyan-400/20 bg-cyan-400/5 relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2.5 rounded-lg bg-black/20 border border-cyan-400/30">
                    <CalendarDays size={18} style={{ color: 'var(--brand-primary)' }} />
                  </div>
                  <h4 className="text-sm font-semibold text-[var(--text-primary)]">Book a Quick Meeting</h4>
                </div>
                <p className="text-xs text-[var(--text-secondary)] leading-relaxed mb-4">
                  Prefer a direct discussion? Schedule a 15-30 minute call to discuss your project scope, timeline, and next steps.
                </p>
                <div className="mb-4 space-y-3">
                  <div>
                    <label className="text-xs text-[var(--text-muted)] mb-1 block">Choose Day</label>
                    <input
                      type="date"
                      value={selectedMeetingDate}
                      min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                      onChange={(e) => setSelectedMeetingDate(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-white outline-none"
                    />
                  </div>
                  <div>
                    <p className="text-xs text-[var(--text-muted)] mb-2">Choose Time</p>
                    <div className="grid grid-cols-3 gap-2">
                      {meetingTimeSlots.map((slot) => (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => setSelectedMeetingTime(slot)}
                          className="rounded-md py-1.5 text-xs font-medium transition-all duration-200"
                          style={{
                            background: selectedMeetingTime === slot ? 'rgba(0, 217, 255, 0.25)' : 'rgba(255, 255, 255, 0.08)',
                            border: selectedMeetingTime === slot ? '1px solid var(--brand-primary)' : '1px solid rgba(255, 255, 255, 0.15)',
                            color: selectedMeetingTime === slot ? 'var(--brand-primary)' : 'var(--text-secondary)'
                          }}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <a
                  href={meetingRequestHref}
                  className="inline-flex items-center justify-center gap-2 w-full py-2.5 rounded-lg text-sm font-medium transition-all duration-300"
                  style={{
                    background: 'linear-gradient(135deg, var(--brand-primary), #00FF88)',
                    color: '#031016'
                  }}
                >
                  <Video size={16} />
                  Request This Slot
                </a>
                <a
                  href={MEETING_URL}
                  target={MEETING_URL.startsWith('http') ? '_blank' : undefined}
                  rel={MEETING_URL.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="inline-flex items-center justify-center gap-2 w-full mt-2 py-2.5 rounded-lg text-xs font-medium transition-all duration-300"
                  style={{
                    background: 'rgba(255, 255, 255, 0.08)',
                    color: 'var(--text-secondary)',
                    border: '1px solid rgba(255, 255, 255, 0.15)'
                  }}
                >
                  <CalendarDays size={14} />
                  Open Full Calendar
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form (Right) */}
          <div className={`lg:col-span-3 ${isVisible ? 'animate-fade-left' : 'opacity-0'}`} style={{ animationDelay: '0.4s' }}>
            <form onSubmit={handleSubmit} className="glass-card p-8 md:p-10 rounded-2xl space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Name */}
                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm text-[var(--text-secondary)] ml-1">Your Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => setFocusedField(null)}
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white outline-none transition-all duration-300 placeholder:text-gray-600"
                    placeholder="John Doe"
                    style={{
                      borderColor: focusedField === 'name' ? 'var(--brand-primary)' : 'var(--border-medium)',
                      boxShadow: focusedField === 'name' ? '0 0 0 4px rgba(0, 217, 255, 0.1)' : 'none'
                    }}
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm text-[var(--text-secondary)] ml-1">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    required
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white outline-none transition-all duration-300 placeholder:text-gray-600"
                    placeholder="john@example.com"
                    style={{
                      borderColor: focusedField === 'email' ? 'var(--brand-primary)' : 'var(--border-medium)',
                      boxShadow: focusedField === 'email' ? '0 0 0 4px rgba(0, 217, 255, 0.1)' : 'none'
                    }}
                  />
                </div>
              </div>

              {/* Subject */}
              <div className="space-y-2">
                <label htmlFor="subject" className="text-sm text-[var(--text-secondary)] ml-1">Subject</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('subject')}
                  onBlur={() => setFocusedField(null)}
                  required
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white outline-none transition-all duration-300 placeholder:text-gray-600"
                  placeholder="Project Inquiry"
                  style={{
                    borderColor: focusedField === 'subject' ? 'var(--brand-primary)' : 'var(--border-medium)',
                    boxShadow: focusedField === 'subject' ? '0 0 0 4px rgba(0, 217, 255, 0.1)' : 'none'
                  }}
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <label htmlFor="message" className="text-sm text-[var(--text-secondary)] ml-1">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('message')}
                  onBlur={() => setFocusedField(null)}
                  required
                  rows="5"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white outline-none transition-all duration-300 placeholder:text-gray-600 resize-none"
                  placeholder="Tell me about your project..."
                  style={{
                    borderColor: focusedField === 'message' ? 'var(--brand-primary)' : 'var(--border-medium)',
                    boxShadow: focusedField === 'message' ? '0 0 0 4px rgba(0, 217, 255, 0.1)' : 'none'
                  }}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 rounded-xl font-bold text-black flex items-center justify-center gap-2 transition-all duration-300"
                style={{
                  background: 'var(--brand-primary)',
                  opacity: isSubmitting ? 0.7 : 1,
                  transform: isSubmitting ? 'scale(0.98)' : 'scale(1)'
                }}
                onMouseEnter={(e) => {
                   if(!isSubmitting) {
                     e.target.style.boxShadow = '0 0 20px rgba(0, 217, 255, 0.4)';
                     e.target.style.transform = 'translateY(-2px)';
                   }
                }}
                onMouseLeave={(e) => {
                   if(!isSubmitting) {
                     e.target.style.boxShadow = 'none';
                     e.target.style.transform = 'translateY(0)';
                   }
                }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      <style jsx>{`
        .glass-card {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .animate-fade-down { animation: fadeDown 0.8s ease-out forwards; }
        .animate-fade-right { animation: fadeRight 0.8s ease-out forwards; }
        .animate-fade-left { animation: fadeLeft 0.8s ease-out forwards; }

        @keyframes fadeDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeRight {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeLeft {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </section>
  );
};

export default Contact;